import { NgIf } from '@angular/common';
import { Component, forwardRef, HostBinding, Input, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatSliderDragEvent, MatSliderModule } from '@angular/material/slider';
import { NgxErrorsModule } from '@ngspot/ngx-errors';
import { TranslateModule } from '@ngx-translate/core';
import { BaseFormAccessor, FormAccessor, NgxValidators } from '@studiohyperdrive/ngx-forms';
import { debounce } from 'lodash';
import { distinctUntilChanged, filter, switchMap, takeUntil, tap } from 'rxjs';

import { CypressTagDirective } from '@cjm/cypress/core';
import { ButtonClasses } from '@cjm/shared/ui/common';
import { CheckBoxComponent, InputWrapperComponent } from '@cjm/shared/ui/forms';

import { AssociationTargetAudience } from '../../../data';
import { I18nKeys } from '../../../i18n';

import { TargetAudienceSelectorForm, TargetAudienceSelectorFormGroup } from './target-audience-selector.types';

@Component({
	selector: 'vloket-target-audience-selector',
	templateUrl: './target-audience-selector.component.html',
	styleUrl: './target-audience-selector.component.scss',
	imports: [
		CheckBoxComponent,
		InputWrapperComponent,
		NgxErrorsModule,
		ReactiveFormsModule,
		TranslateModule,
		NgIf,
		MatSliderModule,
		CypressTagDirective
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => TargetAudienceSelectorComponent),
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => TargetAudienceSelectorComponent),
			multi: true
		},
		{
			provide: BaseFormAccessor,
			useExisting: forwardRef(() => TargetAudienceSelectorComponent)
		}
	]
})
export class TargetAudienceSelectorComponent
	extends FormAccessor<TargetAudienceSelectorForm, FormGroup<TargetAudienceSelectorFormGroup>>
	implements OnInit
{
	@HostBinding('class.c-target-audience-selector') private readonly rootClass: boolean = true;

	@Input() public withSlider: boolean = false;
	@Input() public withAllAgesCheckbox: boolean = true;
	@Input() public withFullRange: boolean = false;

	public readonly initialized: Signal<boolean> = toSignal(this.initialized$);
	public readonly buttonClasses: typeof ButtonClasses = ButtonClasses;
	public readonly associationTargetAudience: typeof AssociationTargetAudience = AssociationTargetAudience;
	public readonly i18nKeys: typeof I18nKeys = I18nKeys;

	public ngOnInit(): void {
		this.initialized$
			.pipe(
				filter((initialized: boolean) => initialized),
				switchMap(() => this.form.get('noAgeLimit').valueChanges),
				distinctUntilChanged(),
				tap((noAgeLimit: boolean) => {
					this.setAgeLimit(noAgeLimit);
				}),
				takeUntil(this.destroy$)
			)
			.subscribe();

		// Denis: When using the ngOnInit lifecycle, do not forget to invoke the super.ngOnInit() method.
		super.ngOnInit();
	}

	// Denis: See: @studiohyperdrive/ngx-forms
	public initForm(): FormGroup<TargetAudienceSelectorFormGroup> {
		return this.buildBasicFormGroup();
	}

	// Denis: See: @studiohyperdrive/ngx-forms
	public writeValue(value: TargetAudienceSelectorForm | undefined | null) {
		super.writeValue(value);

		if (!this.form) {
			return;
		}

		const noAgeLimit = this.form.get('noAgeLimit').value;

		this.setAgeLimit(noAgeLimit);
	}

	/**
	 * writeSliderMinValue
	 *
	 * The writeSliderMinValue method is a helper method to update the minAge form control value.
	 *
	 * @param value{number}
	 * @private
	 */
	private writeSliderMinValue({ value }: MatSliderDragEvent): void {
		if (value === this.form.get('minAge').value) {
			return;
		}

		this.form.get('minAge').setValue(value);
	}
	// Denis: minValueSliderChange is debounced to prevent multiple calls to the writeSliderMinValue method.
	public minValueSliderChange = debounce((value: MatSliderDragEvent) => {
		this.writeSliderMinValue(value);
	}, 600);

	/**
	 * writeSliderMaxValue
	 *
	 * The writeSliderMaxValue method is a helper method to update the maxAge form control value.
	 *
	 * @param value{number}
	 * @private
	 */
	private writeSliderMaxValue({ value }: MatSliderDragEvent): void {
		if (value === this.form.get('maxAge').value) {
			return;
		}

		this.form.get('maxAge').setValue(value);
	}
	// Denis: maxValueSliderChange is debounced to prevent multiple calls to the writeSliderMaxValue method.
	public maxValueSliderChange = debounce((value: MatSliderDragEvent) => {
		this.writeSliderMaxValue(value);
	}, 600);

	/**
	 * buildBasicFormGroup
	 *
	 * The buildBasicFormGroup method is a helper method to create a new FormGroup instance.
	 * This is called in the initForm to build the initial form.
	 *
	 * @private
	 * @returns {FormGroup<TargetAudienceSelectorFormGroup>}
	 */
	private buildBasicFormGroup(): FormGroup<TargetAudienceSelectorFormGroup> {
		return new FormGroup<TargetAudienceSelectorFormGroup>(
			{
				noAgeLimit: new FormControl<boolean>(false),
				minAge: new FormControl(this.associationTargetAudience.MIN_AGE, {
					updateOn: 'blur'
				}),
				maxAge: new FormControl(this.associationTargetAudience.DEFAULT_MAX_AGE, {
					updateOn: 'blur'
				})
			},
			{
				validators: [
					NgxValidators.compareValidator<number>(['minAge', 'maxAge'], (a: number, b: number) => a > b)
				]
			}
		);
	}

	/**
	 * setAgeLimit
	 *
	 * The setAgeLimit method is a helper method to enable/disable the minAge and maxAge form controls
	 *
	 * @param noAgeLimit{boolean}
	 * @private
	 */
	private setAgeLimit(noAgeLimit: boolean): void {
		if (noAgeLimit) {
			this.form.get('minAge').disable();
			this.form.get('maxAge').disable();

			return;
		}

		this.form.get('minAge').enable();
		this.form.get('maxAge').enable();
	}
}
